import { unsafeCoerce } from 'fp-ts/lib/function';
import { useCallback, useRef, useState } from 'react';
import { useOptionalPreloadSlot } from '../../UnicronApp/preloader/PreloadSlot';
import { logRejectionsToSentry } from '../Sentry';
import { useExposedToWindow } from '../hooks/useExposedToWindow';
import { useRouter } from '../hooks/useRouter';
import { useBrowser } from '../services/BrowserService';
import { useLogger } from '../services/LoggerService/LoggerService';
import { ChildrenOnly } from '../types/ChildrenOnly';
import { ExperimentContext } from './ExperimentContext';
import { resolveClientExperiment } from './client/resolveClientExperiment';
import { ExperimentSlot } from './preload/ExperimentSlot';
import { Experiment } from './types';

declare global {
  interface Window {
    overrideExperiment?(key: string, value: unknown): void;
  }
}

export const ExperimentProvider = ({ children }: ChildrenOnly) => {
  const { window } = useBrowser();
  const logger = useLogger();
  const router = useRouter();

  const preloadedExperimentCache = useOptionalPreloadSlot(ExperimentSlot);
  const [experimentCache, setExperimentCache] = useState(preloadedExperimentCache);
  const executedMboxes = useRef(new Set<string>());
  const warnedKeys = useRef(new Set<string>());

  router?.events?.on('routeChangeComplete', () => {
    executedMboxes.current.clear();
  });

  const overrideExperiment = useCallback(
    (key: string, value: unknown) => setExperimentCache({ ...experimentCache, [key]: { value } }),
    [experimentCache, setExperimentCache],
  );

  /** Provide a global `window.overrideExperiment()` dev tool */
  useExposedToWindow('overrideExperiment', overrideExperiment);

  /** By design, this function changes identity whenever the experiment cache changes.
   *
   * This means that consumers will get a new copy of the function and will call it again
   * whenever data becomes available.
   */
  const requestExperiment: ExperimentContext = useCallback(
    (experiment: Experiment<unknown>) => {
      if (window && !executedMboxes.current.has(experiment.mbox)) {
        /** We are in the browser, so we need to execute the mbox regardless of whether it was preloaded */
        executedMboxes.current.add(experiment.mbox);

        logRejectionsToSentry('Fetching client experiment', async () => {
          const value = await resolveClientExperiment(experiment, window);

          setExperimentCache(currentCache => {
            const currentEntry = currentCache[experiment.mbox];

            if (currentEntry && JSON.stringify(currentEntry.value) !== JSON.stringify(value)) {
              /** The frontend is seeing a different value from the backend. This probably indicates a
               * problem with our handling of cookies.
               */
              // TODO: SSR is broken
              // https://hejira.atlassian.net/browse/ONION-400
              //   logErrorToSentry(
              //   `Experiment value mismatch between server and client for mbox "${experiment.mbox}"`,
              //   { serverValue: currentEntry.value, clientValue: value },
              // );
            }

            return {
              ...currentCache,
              [experiment.mbox]: { value },
            };
          });
        });
      }

      const cacheEntry = experimentCache[experiment.mbox];

      if (!window && !cacheEntry && !warnedKeys.current.has(experiment.mbox)) {
        /** We are server-rendering an experiment, but we did not preload it */
        warnedKeys.current.add(experiment.mbox);
        logger?.warn(`Experiment cache miss: "${experiment.mbox}"`);
      }

      return cacheEntry
        ? { loading: false, data: unsafeCoerce(cacheEntry.value) }
        : { loading: true };
    },
    [experimentCache, logger, window],
  );

  return (
    <ExperimentContext.Provider value={requestExperiment}>{children}</ExperimentContext.Provider>
  );
};
